import { useState } from "react";
import { Check } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { cycleLabel, gstNote, inr, useApi } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Btn, PageHero, Reveal, Spinner } from "@/components/site/kit";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { FaqList } from "@/components/site/FaqList";
import { cn } from "@/lib/utils";

const PlanCard = ({ p, i, onBuy, note }) => (
  <Reveal delay={i * 0.08} className="h-full">
    <div className={cn("relative flex h-full flex-col rounded-2xl border p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1", p.popular ? "indigo-gradient border-transparent text-white shadow-lift" : "card-base hover:shadow-lift")} data-testid={`plan-card-${i}`}>
      {p.popular && <span className="absolute -top-3 left-7 rounded-full bg-cta px-3 py-1 text-xs font-bold text-navy" data-testid={`plan-popular-badge-${i}`}>Most Popular</span>}
      <h3 className={cn("text-xl font-bold", p.popular && "text-white")}>{p.name}</h3>
      <p className={cn("mt-2 min-h-[40px] text-sm", p.popular ? "text-white/75" : "text-body")}>{p.description}</p>
      <div className="mt-6">
        {p.price ? (
          <>
            <span className={cn("font-heading text-4xl font-extrabold", p.popular ? "text-white" : "text-navy")} data-testid={`plan-price-${i}`}>{inr(p.price)}</span>
            <span className={cn("ml-1 text-sm", p.popular ? "text-white/70" : "text-muted2")}>{cycleLabel(p.billing_cycle)}</span>
            <p className={cn("mt-1 text-xs", p.popular ? "text-white/60" : "text-muted2")}>{note}</p>
          </>
        ) : <span className={cn("font-heading text-3xl font-extrabold", p.popular ? "text-white" : "text-navy")}>Custom quote</span>}
      </div>
      <ul className="mt-6 flex-1 space-y-3">
        {(p.features || []).map((f) => <li key={f} className={cn("flex gap-2.5 text-sm", p.popular ? "text-white/85" : "text-body")}><Check className={cn("mt-0.5 h-4 w-4 shrink-0", p.popular ? "text-cta" : "text-success")} />{f}</li>)}
      </ul>
      {p.cta_type === "buy" && p.price ? (
        <Btn onClick={() => onBuy(p)} className="mt-8 w-full" testId={`plan-buy-btn-${i}`}>Buy Now</Btn>
      ) : (
        <Btn to="/contact#quote" variant="secondary" className="mt-8 w-full" testId={`plan-contact-btn-${i}`}>Contact Us</Btn>
      )}
    </div>
  </Reveal>
);

export default function Pricing() {
  useSeo({ title: "Pricing", description: "Transparent INR pricing for websites, CRM, automation and custom software. GST invoices, no setup fee." });
  const { settings } = useSite();
  const { data, isLoading } = useApi("/plans");
  const { data: pc } = useApi("/content/pricing");
  const [item, setItem] = useState(null);
  const note = gstNote(settings);

  return (
    <>
      <PageHero badge="Pricing" title={pc?.headline || "Simple, transparent"} highlight={pc?.highlight || "pricing in INR"} subtitle={`${pc?.subtitle || ""} Prices shown are ${settings?.gst_mode === "inclusive" ? "inclusive" : "exclusive"} of GST.`} />
      <section className="section bg-page">
        <div className="container-x">
          {isLoading ? <Spinner /> : (
            <div className="grid gap-6 pt-3 md:grid-cols-2 xl:grid-cols-4" data-testid="pricing-grid">
              {(data || []).map((p, i) => <PlanCard key={p.id} p={p} i={i} note={note} onBuy={(x) => setItem({ type: "plan", id: x.id, name: x.name, price: x.price, billing_cycle: x.billing_cycle })} />)}
            </div>
          )}
          <p className="mt-10 text-center text-sm text-muted2" data-testid="pricing-gst-note">{pc?.note || "All prices in Indian Rupees (₹)."} {note}. See our <a href="/refund-policy" className="font-semibold text-ocean hover:underline">Refund Policy</a>.</p>
        </div>
      </section>
      <section className="section bg-white">
        <div className="container-x mx-auto max-w-3xl"><FaqList category="home" testId="pricing-faq" /></div>
      </section>
      <CheckoutDialog item={item} open={!!item} onOpenChange={(v) => !v && setItem(null)} />
    </>
  );
}
